'use client';

/**
 * SiteFooter — quiet closing surface of the laboratory. Localized links and
 * labels; internal hrefs carry the active locale prefix.
 *
 * The ambient-agent note at the bottom of the identity column is
 * freshness-aware: it reads the live bridge's current state from the world
 * store (same `displayFreshness` rule as the terminal and the 3D field) so
 * it never claims "simulation" while the site is actually showing a
 * chain-verified population.
 *
 * This is a Client Component, so its FIRST rendered bytes — the ones in the
 * statically generated HTML of every page, the ones a crawler reads, the
 * ones a visitor with no working SSE stream keeps — are rendered from the
 * store's initial state. That state knows nothing yet, and the note must say
 * nothing yet: `displayFreshness` maps it to SYNCING, whose copy claims
 * neither "real" nor "simulated". It used to collapse to OFFLINE, which put
 * "Ambient agent telemetry on this site is a labeled simulation." into the
 * static HTML of every page and directly under a LIVE badge on /agents.
 */

import Link from 'next/link';
import { allNav, site } from '../../content/site';
import { localePath } from '../../i18n/config';
import { useLang } from '../../i18n/client';
import { getDict } from '../../i18n/dictionary';
import { useWorld } from '../../lab/store';
import { displayFreshness, showsCanary } from '../../lab/freshness-label';
import s from './SiteFooter.module.css';

export default function SiteFooter() {
  const lang = useLang();
  const dict = getDict(lang);
  const rosterLive = useWorld((w) => w.rosterLive);
  const freshness = useWorld((w) => w.freshness);
  const liveMode = useWorld((w) => w.liveMode);
  const display = displayFreshness(rosterLive, freshness);
  const canary = showsCanary(display, liveMode);

  return (
    <footer className={s.footer}>
      <div className={`wrap ${s.inner}`}>
        <div className={s.cols}>
          {/* ── identity ── */}
          <div className={s.colIdentity}>
            <p className={s.wordmark}>STUDYLABPRO</p>
            <p className={s.tagline}>{dict.footer.tagline}</p>
            <p className={`machine--sm ${s.liveNote}`}>
              {dict.footer.liveNote[display]}
              {canary ? ` ${dict.footer.canaryNote}` : ''}
            </p>
          </div>

          {/* ── sections ── */}
          <nav className={s.col} aria-label={dict.footer.footerAria}>
            <p className={`label ${s.colLabel}`}>{dict.footer.sections}</p>
            <ul className={s.list}>
              {allNav.map((item) => (
                <li key={item.href}>
                  <Link href={localePath(lang, item.href)} className={s.link}>
                    {dict.nav[item.key]}
                  </Link>
                </li>
              ))}
              <li>
                <Link href={localePath(lang, '/privacy')} className={s.link}>
                  {dict.consent.privacyLink}
                </Link>
              </li>
            </ul>
          </nav>

          {/* ── elsewhere ── */}
          <div className={s.col}>
            <p className={`label ${s.colLabel}`}>{dict.footer.elsewhere}</p>
            <ul className={s.list}>
              <li>
                <a
                  href={site.github}
                  className={s.link}
                  rel="noopener noreferrer"
                  target="_blank"
                >
                  GitHub
                </a>
              </li>
              <li>
                <a href={`mailto:${site.email}`} className={s.link}>
                  {site.email}
                </a>
              </li>
              <li>
                <a
                  href={site.commercialArm.href}
                  className={s.link}
                  rel="noopener noreferrer"
                  target="_blank"
                >
                  {site.commercialArm.name}
                </a>
              </li>
              <li>
                <a
                  href={site.product.href}
                  className={s.link}
                  rel="noopener noreferrer"
                  target="_blank"
                >
                  {site.product.name}
                </a>
              </li>
            </ul>
          </div>
        </div>

        <p className={`machine--sm ${s.bottom}`}>
          © 2026 StudyLabPro · {dict.footer.rights}
        </p>
      </div>
    </footer>
  );
}
